export type InventorySummary = {
  id: number
  skuGroupCount: number
  siteCode: string
  siteKey: number
  safetyStockStatus: string
  // From the API (utils/input_changes.py): when this site's replenishment
  // settings last changed and on which page, whether its calculated safety
  // stock predates that, and how many of its coupled rows still hold an old
  // template cycle.
  inputsChangedAt?: number | null
  inputsChangedBy?: string | null
  safetyStockStale?: boolean
  coupledCyclesOutOfDate?: number
}

/** Why a site's safety stock is out of date, or null when it isn't. */
export const safetyStockWarning = (site: InventorySummary): string | null => {
  const reasons: string[] = []
  if (site.safetyStockStale) {
    const when = site.inputsChangedAt
      ? new Date(site.inputsChangedAt).toLocaleString(undefined, {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      : 'recently'
    const where = site.inputsChangedBy ? ` on ${site.inputsChangedBy}` : ''
    reasons.push(
      `Its replenishment settings changed${where} (${when}) after this was calculated. Calculate again to update it.`
    )
  }
  const waiting = site.coupledCyclesOutOfDate ?? 0
  if (waiting > 0) {
    reasons.push(
      `${waiting} coupled row${waiting === 1 ? '' : 's'} still use an old template cycle. Update coupled cycles on Replenishment, then calculate again.`
    )
  }
  return reasons.length ? reasons.join(' ') : null
}

export type InventoryResult = {
  baseSourceSiteKey?: number
  baseSourceSiteCode?: string
  siteKey?: number
  siteCode?: string
  skuGroupKey?: number
  skuGroupCode?: string
  segment?: string
  minimumOrderQuantity?: number
  stockPeriod?: number
  serviceLevel?: number
  replenishmentCycleTime?: number
  replenishmentTypeCode?: string
  supplyTypeCode?: string
  actualBAUVolume?: number
  actualSafetyStockVolume?: number
  actualCycleStockVolume?: number
  cycleStockVolume?: number
  hybridSafetyStockVolume?: number
  unusedInventory?: number
  actualInventoryValue?: number
  costPerUnit?: number
  forecastAccuracyByForecastError?: number
  forecastAccuracyByDemandVariability?: number
  ssVolumeByForecastError?: number
  ssVolumeByDemandVariability?: number
  weeklySalesRateByForecastError?: number
  weeklySalesRateByDemandVariability?: number
  dailySalesRate?: number
  forecastAccuracy?: number
  actualBAUVolumeInPallets?: number
  cycleStockVolumeInPallets?: number
  unusedInventoryInPallets?: number
  ssVolumeByForecastErrorInPallets?: number
  ssVolumeByDemandVariabilityInPallets?: number
  chosenMethod?: string
  safetyStockType?: string
}
